<template>
    <VButton
        class="send-bill-receipt-btn primary rounded"
        :class="{ loading: isLoading }"
        @click="sendBillPaymentReceipt"
        :disabled="isDisabled"
    >
        {{ translations.receipt.sendReceipt }}
    </VButton>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import { Validation } from "@/data/util/Validation";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, ref } from "vue";

    import VButton from "@/components/elements/VButton.vue";

    // Props definition
    const props = defineProps<{
        emailRecipient: string | undefined;
        paymentId: string;
        locationId: string;
        onSendReceiptSuccess?: () => void;
        onInvalidEmailProvided?: () => void;
        alwaysEnabled?: boolean;
    }>();

    // State
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const isLoading = ref(false);

    // Computed properties
    const validEmail = computed(() =>
        props.emailRecipient && Validation.isEmail(props.emailRecipient) ? props.emailRecipient : undefined
    );
    const isInvalidEmail = computed(() => validEmail.value == null);
    const isDisabled = computed(() =>
        props.alwaysEnabled ? isLoading.value : isLoading.value || isInvalidEmail.value
    );

    // Methods
    async function sendBillPaymentReceipt() {
        if (isLoading.value) return;
        if (isInvalidEmail.value) {
            if (props.onInvalidEmailProvided) {
                props.onInvalidEmailProvided();
            }
            return;
        }

        try {
            isLoading.value = true;
            const receiver = validEmail.value!;
            await customerDao.sendBillReceipt(props.paymentId, {
                email: receiver,
            });

            Popup.show({
                message: translations.receipt.sentReceiptTo.replace("{0}", receiver.toLowerCase()),
                confirmHandler: () => {
                    if (props.onSendReceiptSuccess) {
                        props.onSendReceiptSuccess();
                    }
                    isLoading.value = false;
                },
            });
        } catch (ex) {
            logError(ex);
            Popup.show({ message: translations.receipt.failedSendReceipt });
            isLoading.value = false;
        }
    }

    // define exposed
    defineExpose({
        sendReceipt: sendBillPaymentReceipt,
    });
</script>

<style lang="scss">
    .send-bill-receipt-btn.v-btn.primary {
        font-size: 17px;
        margin-top: 0.625em;
        height: 2.75em;
        line-height: 2.75em;
        padding: 0 2em;
        border-radius: 1.5em !important;
        width: 100%;
    }
</style>
